import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { buildClassName } from '@bluebeam/build-class-name';
import { useDataAttributes } from '@bluebeam/react-hooks';
import { GeneralIcons } from '@bluebeam/react-skyline-art';
import { Chip } from '@bluebeam/react-skyline-chip';
import { InfoTooltip } from '@bluebeam/react-skyline-info-tooltip';
import { Label } from '@bluebeam/react-skyline-label';
import { Popover } from '@bluebeam/react-skyline-popover';
import { useOverlay, useOverlayPosition } from '@react-aria/overlays';
import { mergeProps, useId } from '@react-aria/utils';
import { useEffect, useMemo, useRef } from 'react';
import ComboboxEmpty from './ComboboxEmpty';
import ComboboxOption from './ComboboxOption';
import ComboboxUnlisted from './ComboboxUnlisted';
import useAutocomplete from './useAutocomplete';
const Combobox = ({ children, errorMessage, fullWidth = false, isDisabled = false, isRequired = false, label, maxHeight, minHeight, multiple = false, onChange, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
placeholder, popoverMaxHeight = 252, tooltip, value, ...rest }) => {
    const optionNodes = useMemo(() => (Array.isArray(children) ? children : [children]).reduce((nodeMap, { props: { children, disabled, label, value } }) => {
        if ((value && label) || children) {
            nodeMap.set(value, {
                children,
                disabled,
                label: label || children,
            });
        }
        return nodeMap;
    }, new Map()), [children]);
    const optionList = useMemo(() => (Array.isArray(children) ? children : [children]).flatMap(({ props: { children, label, value } }) => {
        return (value && children) || label ? { label: label || children, value } : [];
    }), [children]);
    const { focused: isFocused, focusedTag, getInputLabelProps, getInputProps, getListboxProps, getOptionProps, getPopupIndicatorProps, getRootProps, setFocusedTag, getTagProps, handleClose, groupedOptions: filteredOptions, popupOpen: isOpen, setAnchorEl, setAnchorPaddingEl, } = useAutocomplete({
        freeSolo: false,
        filterOptions: (options, state) => {
            const query = state.inputValue.toLowerCase();
            return options.filter((option) => {
                return (option.label.toLowerCase().includes(query) || option.value.toLowerCase().includes(query));
            });
        },
        isOptionEqualToValue: (option, value) => {
            return option.value === value;
        },
        multiple,
        options: optionList,
        optionFocusedClassName: 'bg-primary-blue/10',
        onChange: (_, selection) => {
            onChange(selection);
        },
        value,
    });
    const popoverRef = useRef(null);
    const targetRef = useRef(null);
    const { overlayProps } = useOverlay({
        isOpen,
    }, popoverRef);
    const { overlayProps: positionProps } = useOverlayPosition({
        isOpen,
        containerPadding: 0,
        maxHeight: popoverMaxHeight,
        offset: 2,
        overlayRef: popoverRef,
        onClose: handleClose,
        placement: 'bottom',
        targetRef,
    });
    const dataAttributes = useDataAttributes(rest);
    useEffect(() => {
        // When the popover opens, set its vertical scroll position to the first selected item.
        // From https://www.w3.org/WAI/ARIA/apg/patterns/listbox/:
        // "If one or more options are selected before the listbox receives focus,
        // focus is set on the first option in the list that is selected."
        if (isOpen && popoverRef.current) {
            const selectedValues = multiple ? value : [value];
            if (selectedValues.length) {
                const scrollOffset = selectedValues
                    .map((selectedValue) => {
                    const el = popoverRef.current.querySelector(`li[data-option-value="${selectedValue}"]`);
                    return el ? el.offsetTop : 0;
                })
                    .reduce((lowestOffset, currentOffset) => Math.min(lowestOffset, currentOffset));
                if (popoverRef.current.scrollTo) {
                    popoverRef.current.scrollTo(0, scrollOffset);
                }
            }
        }
    }, [isOpen, multiple, popoverRef, value]);
    const rootProps = getRootProps();
    const inputProps = getInputProps();
    const labelProps = getInputLabelProps();
    const buttonProps = getPopupIndicatorProps();
    const listboxProps = getListboxProps();
    const errorId = useId();
    // useAutocomplete expects the listbox to scroll, not its container,
    // so move the maxHeight directive from Popover's positionProps to the
    // UL element's listboxProps:
    listboxProps.style = {
        width: targetRef.current?.clientWidth,
        maxHeight: positionProps.style.maxHeight,
    };
    positionProps.style.maxHeight = undefined;
    return (_jsxs("div", { ...rootProps, "aria-describedby": errorMessage ? errorId : undefined, "aria-required": isRequired ? 'true' : undefined, ...dataAttributes, className: fullWidth ? 'w-full' : 'max-w-[400px]', children: [_jsxs(Label, { ...labelProps, children: [isRequired ? `${label}*` : label, tooltip && _jsx(InfoTooltip, { tooltip: tooltip })] }), _jsx("div", { className: buildClassName('rounded-lg border border-solid bg-white', 'border-[1px] shadow-[0_0_0_1px]', isFocused && !isDisabled
                    ? 'shadow-primary-blue border-primary-blue'
                    : errorMessage
                        ? 'border-alerts-error-red shadow-alerts-error-red focus-within:border-alerts-error-red focus-within:shadow-alerts-error-red'
                        : 'border-darker-gray shadow-transparent', { 'border-darker-gray/20': isDisabled }), ref: targetRef, children: _jsxs("div", { className: "m-0 flex p-0", children: [_jsxs("div", { className: buildClassName('scrollbar-hide box-border w-full overflow-auto', 'px-2 py-0', { 'opacity-20': isDisabled }), ref: setAnchorEl, style: {
                                minHeight: minHeight && `${minHeight}px`,
                                maxHeight: maxHeight && `${maxHeight}px`,
                            }, children: [_jsxs("div", { className: "m-0 flex flex-wrap items-center p-0", children: [multiple &&
                                            value.flatMap((selectedValue, index) => {
                                                const { 'data-tag-index': dataTagIndex, key, onDelete, tabIndex, } = getTagProps({ index });
                                                const isSelected = focusedTag === dataTagIndex;
                                                const optionNode = optionNodes.get(selectedValue);
                                                if (!optionNode) {
                                                    // There's no matching option for this value, so don't display a Chip.
                                                    // flatMap will exclude this empty array from the result.
                                                    return [];
                                                }
                                                return (_jsx(Chip, { className: buildClassName({
                                                        'bg-gray hover:bg-gray text-black': isDisabled,
                                                        italic: !optionNode.children,
                                                    }), "data-tag-index": dataTagIndex, isDisabled: isDisabled, onRemove: onDelete, tabIndex: tabIndex, isSelected: isSelected, onClick: () => {
                                                        if (inputProps.ref.current) {
                                                            inputProps.ref.current.blur();
                                                        }
                                                        if (isSelected) {
                                                            setFocusedTag(-1);
                                                        }
                                                        else if (!isDisabled) {
                                                            setFocusedTag(dataTagIndex);
                                                        }
                                                    }, children: optionNode.label }, key));
                                            }), _jsx("input", { ...inputProps, "aria-autocomplete": "none", autoComplete: "off", className: buildClassName('skyline-text-body-1 inline-flex w-0 min-w-[2rem] grow border-none px-0 py-[9px] text-black outline-none'), disabled: isDisabled, spellCheck: "false" })] }), _jsx("div", { children: multiple && (_jsx("div", { className: "h-0", children: _jsx("div", { className: "h-4", ref: setAnchorPaddingEl }) })) })] }), _jsx("button", { ...buttonProps, className: buildClassName('m-0 flex cursor-pointer items-center border-none bg-transparent py-0 pl-0 pr-4 disabled:cursor-default', { 'opacity-20': isDisabled }), disabled: isDisabled, type: "button", children: _jsx(GeneralIcons.ChevronIcon, { direction: isOpen ? 'up' : 'down', size: "24px" }) })] }) }), isOpen ? (_jsx(Popover, { className: "elevation-2", ...mergeProps(overlayProps, positionProps), ref: popoverRef, children: _jsxs("ul", { ...listboxProps, className: "m-0 list-none overflow-auto p-0 first:hover:rounded-t-lg last:hover:rounded-b-lg", children: [filteredOptions.length === 0 ? children : null, filteredOptions.flatMap((filteredOption, index) => {
                            // eslint-disable-next-line @typescript-eslint/no-unused-vars
                            const { key, ...optionProps } = getOptionProps({ index, option: filteredOption });
                            const optionNode = optionNodes.get(filteredOption.value);
                            const isSelected = Boolean(optionProps['aria-selected']);
                            return !optionNode.children ? ([]) : (_jsxs("li", { className: buildClassName('box-border px-4 py-[0.625rem]', 'skyline-text-body-1 focus-visible:bg-primary-blue/10 hover:bg-primary-blue/10 flex min-h-[56px] w-full items-center text-black first:rounded-t-lg last:rounded-b-lg  first:hover:rounded-t-lg last:hover:rounded-b-lg focus:outline-none first:focus-visible:rounded-t-lg last:focus-visible:rounded-b-lg', {
                                    'opacity-20 hover:bg-white': Boolean(optionProps['aria-disabled']),
                                    'justify-between': isSelected,
                                }), "data-option-value": filteredOption.value, ...optionProps, children: [_jsx("div", { children: optionNode.children }), isSelected && (_jsx(GeneralIcons.CheckIcon, { className: "text-primary-blue", size: "24px" }))] }, filteredOption.value));
                        })] }) })) : (errorMessage && (_jsx("p", { id: errorId, className: "skyline-text-body-2 text-alerts-error-red", children: errorMessage })))] }));
};
Combobox.Empty = ComboboxEmpty;
Combobox.Option = ComboboxOption;
Combobox.Unlisted = ComboboxUnlisted;
export default Combobox;
//# sourceMappingURL=Combobox.js.map